import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Table, Badge, Dropdown, Spinner } from 'react-bootstrap';
import { FaBell, FaSearch, FaFilter, FaCog, FaTachometerAlt, FaMapMarkerAlt, FaExclamationTriangle, FaPowerOff, FaShieldAlt, FaFileExport, FaSync } from 'react-icons/fa';
import { GetFromApi, PutToApi } from '../utils/api';
import socketService from '../utils/socket';
import '../styles/Pages.css';

const Alerts = () => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertConfigs, setAlertConfigs] = useState([]);

  const alertTypes = [
    { id: 'speed', name: 'Speed Violation', icon: FaTachometerAlt, color: '#EF4444' },
    { id: 'geofence', name: 'Geofence', icon: FaMapMarkerAlt, color: '#3B82F6' },
    { id: 'sos', name: 'SOS Alert', icon: FaExclamationTriangle, color: '#A855F7' },
    { id: 'ignition', name: 'Ignition', icon: FaPowerOff, color: '#22C55E' },
    { id: 'tamper', name: 'Tamper', icon: FaShieldAlt, color: '#F59E0B' }
  ];

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/alerts');
      if (response.success) {
        setAlerts(response.data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await GetFromApi('/alert-configs');
      if (response.success) {
        setAlertConfigs(response.data);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
    }
  };

  useEffect(() => {
    fetchAlerts();
    fetchConfigs();

    socketService.connect();
    socketService.subscribeToAlerts((data) => {
      if (Array.isArray(data)) {
        setAlerts(data);
      } else {
        setAlerts(prev => [data, ...prev]);
      }
    });

    return () => socketService.off('alert:new');
  }, []);

  const getAlertTypeInfo = (typeId) => alertTypes.find(t => t.id === typeId) || alertTypes[0];

  const filteredAlerts = alerts.filter(alert => {
    const matchSearch = alert.vehicle_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       alert.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || alert.type === filterType;
    return matchSearch && matchType;
  });

  const handleMarkAsRead = async (id) => {
    try {
      await PutToApi(`/alerts/${id}/read`, {});
      setAlerts(alerts.map(a => a.id === id ? { ...a, is_read: true } : a));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await PutToApi('/alerts/mark-all-read', {});
      setAlerts(alerts.map(a => ({ ...a, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleSaveConfigs = async () => {
    try {
      await PutToApi('/alert-configs', { configs: alertConfigs });
      setShowConfigModal(false);
    } catch (error) {
      console.error('Error saving configs:', error);
    }
  };

  const handleToggleConfig = (alertId, field) => {
    setAlertConfigs(alertConfigs.map(c => 
      c.alert_type === alertId ? { ...c, [field]: !c[field] } : c
    ));
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <div className="d-flex align-items-center">
          <h4 className="page-title mb-0">Alerts</h4>
          {unreadCount > 0 && <Badge bg="danger" className="ms-2 rounded-pill">{unreadCount} New</Badge>}
        </div>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchAlerts} disabled={loading} style={{ borderRadius: '8px' }}>
            {loading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
          <Button variant="outline-primary" onClick={handleMarkAllRead} style={{ borderRadius: '8px' }}>Mark All Read</Button>
          <Button variant="primary" onClick={() => setShowConfigModal(true)} style={{ borderRadius: '8px' }}>
            <FaCog className="me-2" /> Configure Alerts
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={2} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}><FaBell /></div>
              <div><div className="stats-value">{alerts.length}</div><div className="stats-label">Total Alerts</div></div>
            </div>
          </Card>
        </Col>
        {alertTypes.slice(0, 4).map(type => {
          const count = alerts.filter(a => a.type === type.id).length;
          const IconComponent = type.icon;
          return (
            <Col key={type.id} md={2} sm={6} className="mb-3">
              <Card className={`stats-card border-0 shadow-sm ${filterType === type.id ? 'border border-2 border-primary' : ''}`}
                style={{ cursor: 'pointer' }} onClick={() => setFilterType(filterType === type.id ? 'all' : type.id)}>
                <div className="d-flex align-items-center">
                  <div className="icon-box me-3" style={{ backgroundColor: `${type.color}20`, color: type.color }}><IconComponent /></div>
                  <div><div className="stats-value">{count}</div><div className="stats-label">{type.name}</div></div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card className="page-card">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold text-dark">Recent Alerts</h6>
          <div className="d-flex gap-2">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <Form.Control type="text" placeholder="Search alerts..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '2.5rem', borderRadius: '8px', width: '250px' }} />
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="light" style={{ borderRadius: '8px' }}><FaFilter className="me-2" /> Filter</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setFilterType('all')} active={filterType === 'all'}>All Alerts</Dropdown.Item>
                {alertTypes.map(type => (
                  <Dropdown.Item key={type.id} onClick={() => setFilterType(type.id)} active={filterType === type.id}>{type.name}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead style={{ backgroundColor: '#f8fafc' }}>
                <tr><th style={{ width: '50px' }}></th><th>Type</th><th>Vehicle</th><th>Message</th><th>Location</th><th>Date & Time</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredAlerts.map(alert => {
                  const typeInfo = getAlertTypeInfo(alert.type);
                  const IconComponent = typeInfo.icon;
                  return (
                    <tr key={alert.id} style={{ backgroundColor: !alert.is_read ? 'rgba(59, 130, 246, 0.03)' : 'white' }}>
                      <td>{!alert.is_read && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3B82F6', display: 'inline-block' }} />}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: `${typeInfo.color}15`, color: typeInfo.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="me-2">
                            <IconComponent size={16} />
                          </div>
                          <span className="fw-medium">{typeInfo.name}</span>
                        </div>
                      </td>
                      <td><span className="fw-semibold">{alert.vehicle_number}</span></td>
                      <td><span className="text-secondary">{alert.message}</span></td>
                      <td><small className="text-muted">{alert.location}</small></td>
                      <td><span className="text-muted">{new Date(alert.created_at).toLocaleString()}</span></td>
                      <td>
                        {!alert.is_read && (
                          <Button variant="link" size="sm" className="text-primary p-0" onClick={() => handleMarkAsRead(alert.id)}>Mark Read</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {filteredAlerts.length === 0 && !loading && (
            <div className="empty-state py-5">
              <div className="empty-state-icon"><FaBell /></div>
              <div className="empty-state-title">No alerts found</div>
              <div className="empty-state-text">Try adjusting your search or filter</div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} centered size="lg" className="modal-custom">
        <Modal.Header closeButton><Modal.Title>Configure Alert Settings</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">Enable or disable alerts and configure notification preferences.</p>
          {alertTypes.map(type => {
            const config = alertConfigs.find(c => c.alert_type === type.id);
            const IconComponent = type.icon;
            return (
              <div key={type.id} className="d-flex align-items-center justify-content-between p-3 mb-3 rounded border">
                <div className="d-flex align-items-center">
                  <div className="me-3" style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: `${type.color}15`, color: type.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconComponent size={20} />
                  </div>
                  <div>
                    <div className="fw-semibold">{type.name}</div>
                    <div className="d-flex gap-3 mt-1">
                      <Form.Check type="checkbox" label="Email" checked={config?.notify_email || false}
                        onChange={() => handleToggleConfig(type.id, 'notify_email')} disabled={!config?.enabled} />
                      <Form.Check type="checkbox" label="SMS" checked={config?.notify_sms || false}
                        onChange={() => handleToggleConfig(type.id, 'notify_sms')} disabled={!config?.enabled} />
                    </div>
                  </div>
                </div>
                <Form.Check type="switch" id={`switch-${type.id}`} checked={config?.enabled || false}
                  onChange={() => handleToggleConfig(type.id, 'enabled')} />
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowConfigModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveConfigs}>Save Settings</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Alerts;
